import {
  authenticatedUserRoutes,
  superUserRoutes,
} from '../routes/allRoutes';

/** Legacy control-panel card keys → route paths (for saved NavPolicyConfig). */
const LEGACY_CARD_KEY_TO_PATH = {
  user_management: '/settings/user_management',
  organisation_management: '/settings/business_organisation_management',
  dsa_compliance: '/settings/business_organisation_data_management',
  upload_sessions: '/settings/upload_sessions',
  print_templates: '/settings/templates',
  smtp_config: '/settings/smtp_config',
  application_users: '/settings/applicationUsers',
  feedback_management: '/settings/feedback',
  nav_policies: '/settings/nav_policies',
  forms: '/settings/forms',
};

export const formatRouteLabel = (path) => {
  const segment = path.split('/').filter(Boolean).pop() || path;
  return segment
    .replace(/^:/, '')
    .replace(/[_-]/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const mapRouteEntry = (route) => ({
  path: route.path,
  label: route.searchLabel || formatRouteLabel(route.path),
  icon: route.icon,
});

const getAuthRoutes = () =>
  [...authenticatedUserRoutes, ...superUserRoutes]
    .filter((route) => route.path && route.path !== '/')
    .map(mapRouteEntry)
    .sort((a, b) => a.label.localeCompare(b.label));

/** Page-level policy targets — auth-protected routes only (no NonAuth pages). */
export const getPagePolicyRoutes = () => getAuthRoutes();

/** Settings control-panel cards — top-level /settings/* routes only (no create/edit/param routes). */
export const getSettingsCardRoutes = () => {
  const routes = superUserRoutes
    .filter(
      (route) =>
        route.path?.startsWith('/settings/')
        && !route.path.includes(':')
        && !route.path.endsWith('/create'),
    )
    .map(mapRouteEntry)
    .sort((a, b) => a.label.localeCompare(b.label));

  const userManagement = routes.filter((route) => route.path === '/settings/user_management');
  return [
    ...userManagement,
    ...routes.filter((route) => route.path !== '/settings/user_management'),
  ];
};

export const migrateCardPolicyKeys = (cards = {}) => {
  const migrated = {};
  Object.entries(cards).forEach(([key, policies]) => {
    const path = key.startsWith('/') ? key : (LEGACY_CARD_KEY_TO_PATH[key] || key);
    migrated[path] = policies;
  });
  return migrated;
};

/**
 * Match a pathname against a route pattern (supports :param segments).
 */
export const matchRoutePattern = (pattern, pathname) => {
  const patternParts = pattern.split('/').filter(Boolean);
  const pathParts = pathname.split('/').filter(Boolean);
  if (patternParts.length !== pathParts.length) return false;
  return patternParts.every(
    (part, index) => part.startsWith(':') || part === pathParts[index],
  );
};

/**
 * Find the best matching route definition for a pathname.
 */
export const findMatchingRoute = (pathname, routes = getPagePolicyRoutes()) => {
  const exact = routes.find((route) => route.path === pathname);
  if (exact) return exact;

  return routes.find((route) => matchRoutePattern(route.path, pathname)) || null;
};
